import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, User, LogOut, Settings, Users, Building2, UserSearch, Home, X, Briefcase, BarChart3, CheckSquare } from 'lucide-react';
import { getAvatarUrl, DEFAULT_AVATAR_ID } from '../config/avatars';

const MobileNavigation = () => {
  const { userProfile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    signOut();
    navigate('/login');
  };

  // Navigation items based on user role
  const getNavigationItems = () => {
    if (!userProfile) return [];

    // Map database roles to frontend role codes
    const roleMap = {
      'jobasti_admin': 'JADM',
      'jobasti_recruiter': 'JREC',
      'client_admin': 'CADM',
      'client_recruiter': 'CREC',
      'prospect': 'PROS',
      'sales_executive': 'SEXE'
    };

    const userRole = roleMap[userProfile.role] || userProfile.role;

    const baseItems = [
      { path: '/dashboard', label: t('appNav.dashboard'), icon: Home, noTranslate: true }
    ];

    switch (userRole) {
      case 'JADM': // Jobasti Admin (Super Admin)
        return [
          { path: '/admin/dashboard', label: t('appNav.dashboard'), icon: Home, noTranslate: true },
          { path: '/admin/companies', label: 'Companies', icon: Building2 },
          { path: '/admin/users', label: 'Users', icon: Users },
          { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },

          { path: '/sourcing', label: 'Sourcing', icon: UserSearch },
          { path: '/admin/settings', label: 'Settings', icon: Settings },
        ];
      case 'SEXE': // Jobasti Sales Executive (internal CRM)
        return [
          { path: '/sales-dashboard', label: t('appNav.dashboard'), icon: Home, noTranslate: true },
          { path: '/accounts', label: 'Accounts', icon: Building2 },
          { path: '/crm-contacts', label: 'Contacts', icon: Users },
          { path: '/crm-activities', label: 'Activities', icon: BarChart3 },
          { path: '/sales-tasks', label: 'Tasks', icon: CheckSquare },
        ];
      case 'CADM': // Client Admin - has Company management
        return [
          ...baseItems,
          { path: '/candidates', label: 'Candidates', icon: Users },
          { path: '/positions', label: 'Jobs', icon: Briefcase },
          { path: '/my-company', label: 'Company', icon: Building2 },
        ];
      case 'CREC': // Client Recruiter
        return [
          ...baseItems,
          { path: '/candidates', label: 'Candidates', icon: Users },
          { path: '/positions', label: 'Jobs', icon: Briefcase },
          { path: '/my-company', label: 'Company', icon: Building2 },
        ];
      case 'PROS': // Prospect
        return [
          { path: '/prospect-dashboard', label: t('appNav.dashboard'), icon: Home, noTranslate: true },
          { path: '/jobs', label: 'Job Search', icon: Briefcase },
          { path: '/my-jobs', label: 'My Jobs', icon: User },
        ];
      default:
        return baseItems;
    }
  };

  const navigationItems = getNavigationItems();

  return (
    <nav className="bg-white shadow-sm border-b lg:hidden">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex-shrink-0">
            <h1 className="text-xl font-bold text-gray-900 notranslate" translate="no">Jobasti<span className="text-blue-600">IQ</span></h1>
          </Link>
          
          <div className="flex items-center space-x-4">
            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center space-x-2 h-auto py-1 px-1.5" data-testid="mobile-user-menu-trigger">
                  <img
                    src={getAvatarUrl(userProfile?.avatarUrl || DEFAULT_AVATAR_ID)}
                    alt="Avatar"
                    className="w-7 h-7 rounded-full object-cover ring-2 ring-gray-200"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">
                    {userProfile?.firstName && userProfile?.lastName
                      ? `${userProfile.firstName} ${userProfile.lastName}`
                      : userProfile?.username || userProfile?.email}
                  </p>
                  <p className="text-xs text-gray-500">{userProfile?.email}</p>
                  <p className="text-xs text-blue-600">
                    {userProfile?.role === 'jobasti_admin' || userProfile?.role === 'JADM' ? 'Admin' : 
                     userProfile?.role === 'client_admin' || userProfile?.role === 'RADM' ? 'Administrator' :
                     userProfile?.role === 'client_recruiter' || userProfile?.role === 'RECR' ? 'Recruiter' : 'Applicant'}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(userProfile?.role === 'prospect' || userProfile?.role === 'PROS' ? '/my-profile' : '/user-profile')}>
                  <User className="w-4 h-4 mr-2" />
                  User Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <SheetHeader>
                  <SheetTitle className="text-left">Navigation</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-2">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-5 h-5 mr-3" />
                        {item.noTranslate ? (
                          <span className="notranslate" translate="no">{item.label}</span>
                        ) : item.label}
                      </Link>
                    );
                  })}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default MobileNavigation;